/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 2 (dev-sync) B1+B2 — the FAN selector's VFD INPUT TAB.
 *
 * Backbone source (fan.selector.tmpl 1148-1565 + fan.selector.js):
 *   changeVfdField 4387-4467, updateVfdMountingPlane 4348-4359,
 *   negative-distance validation 4361-4382, changeParams has_vfd 4621-4630,
 *   motorSupportsVfd/by_motor/smartlist 7854-8054, pagination/select 8060-8283,
 *   helpers/qty/arrangements/voltage/model-refresh 8328-8645,
 *   _buildVfdPayload/computeVFDPosition/applyVFDPositionResponse 8341-8472,
 *   loadVfdSections 8476-8502, updateVfdFieldsEnabledState/_getVFD 8660-8834.
 *
 * DOM classes/ids mirror the Backbone template — the legacy CSS is global
 * (vfd-mount-wrapper flip, left-column rows, smartlist w-* columns, …).
 *
 * B1: tab UI + CMS plumbing + by_motor smartlist + catalog cascade +
 * qty/mounting-arrangement chain + state init.
 * B2 (here too): sections GET, compute_position POST + response merge (bank
 * W×H, Hy/Wx/Lx, position echoes, dynamic enc minimum, negative-distance
 * `warning` class). NOT here:
 *   B3 (built elsewhere) — Select gating + persist + has_vfd stamping live in
 *   FanSelector/fanVfdPersist/fanSelectorData; the canvas bank drawing in
 *   three/canvas3dGeometry.
 *   B4 (later) — VFD output tab + VfdForm read-only viewer.
 * The dead "KA-5168 VFD menu controller" script block (tmpl 1757-2076) is
 * intentionally NOT ported.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDecimalDisplay } from '../formUtils';
import { useLazyGetAccessoryPositionQuery } from 'app/services/ahuBoardApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  VFD_CMS_PARAMS_URL,
  buildVfdByMotorUrl,
  buildVfdConfigurationsUrl,
  buildVfdPayload,
  buildVfdQtyTotalUrl,
  buildVfdMountingArrangementsUrl,
  isVfdNone,
  useGetVfdCmsParamsQuery,
  useGetAhuVfdsQuery,
  useGetAhuSectionsQuery,
  useLazyGetVfdByMotorQuery,
  useLazyGetVfdCmsCountQuery,
  useLazyGetVfdCmsParamsQuery,
  useLazyGetVfdConfigurationsQuery,
  useLazyGetVfdQtyTotalQuery,
  useLazyGetVfdMountingArrangementsQuery,
  usePostVfdComputePositionMutation,
  type AhuSectionRow,
  type AhuVfdRow,
  type VfdByMotorItem,
  type VfdCmsRow,
  type VfdComputeResponse,
  type VfdMountingArrangement,
} from './fanVfdApi';

/** VFD accessory state refs — owned by FanSelector (B3 persists them on
 * Select; B4 reads vfd_params + the qty total for the output tab); seeded +
 * edited here. */
export interface FanVfdRefs {
  vfd: React.MutableRefObject<Record<string, any> | null>;
  vfdPosition: React.MutableRefObject<Record<string, any>>;
  vfdParams: React.MutableRefObject<Record<string, any>>;
  /** legacy self._vfdQtyTotal (js:8571-8577) — the output tab's Quantity. */
  vfdQtyTotal: React.MutableRefObject<number | null>;
}

interface FanVfdTabProps {
  refs: FanVfdRefs;
  ahuId: number | null;
  /** fan.selector.js:2415 `prefix: self.header ? '' : 'ahu-'` — the tab-pane DOM
   * id prefix. `''` in the header "independent rating" host so its ids cannot
   * collide with a board dialog's on the same page (fan.selector.tmpl:1322
   * `id="{{prefix}}vfd-flip"`). */
  idPrefix?: string;
  /** the fan component id — matched against the persisted VFD's fan_motor_id
   * (legacy _getVFD: fan_motor_id === current_model?.id, js:8768-8769). */
  fanComponentId: number;
  /** the fan detail row (legacy model_data.model). */
  detail: Record<string, any> | undefined;
  /** FanSelector's live state ref (legacy self.state). */
  state: React.MutableRefObject<Record<string, unknown>>;
  /** the resolved motor row (legacy motor_choosen || selectedMotorData). */
  selectedMotor: Record<string, any> | null;
  section: { id?: number; label?: string | null; tag?: string | null } | null;
  hasVfd: boolean;
  setHasVfd: (checked: boolean) => void;
  /** state motor_type_id == 1 (js:7854-7857). */
  motorSupportsVfd: boolean;
  /** the VFD tab li's non-clickable state (computed by FanSelector). */
  tabDisabled: boolean;
  /** inputTab === 'vfd' — opening the tab triggers the first smartlist load. */
  isActive: boolean;
  /** qty_wide*qty_high || model fan_qty || 1 (the updateVfdQtyTotal fallback
   * chain, js:8562-8565) — always >0 so by_motor always carries fan_qty. */
  fanQty: number;
  bump: () => void;
}

interface SmartlistState {
  data: VfdByMotorItem[];
  count: number | null;
  cmsCount: number | null;
  isLoading: boolean;
  reqId: number;
  isOpen: boolean;
  page: number;
  hasMore: boolean;
}

// Dev-sync I (fan.selector.js:534-539 on merged dev) — MasterAHUVFDConfiguration
// id for "One to One", the default a fresh VFD starts with (the _getVFD init,
// mirrored below) before the user has touched Mfg/Series, so
// resolveConfigurationValue can fall back to it by value even before the
// mfg/series-filtered configuration list (or the vfdCms fallback) has loaded.
const DEFAULT_VFD_CONFIGURATION_ID = 1;
const DEFAULT_VFD_CONFIGURATION_VALUE = 'one_to_one';

export const FanVfdTab = ({
  refs, ahuId, idPrefix = 'ahu-', fanComponentId, detail, state, selectedMotor, section,
  hasVfd, setHasVfd, motorSupportsVfd, tabDisabled, isActive, fanQty, bump,
}: FanVfdTabProps) => {
  const { vfd: vfdRef, vfdPosition: posRef, vfdParams: paramsRef } = refs;
  // legacy self._vfdQtyTotal — FanSelector-owned so the VFD OUTPUT tab (B4)
  // renders the same Quantity value (render context vfd_qty, js:2835).
  const qtyTotalRef = refs.vfdQtyTotal;

  const { data: vfdCms } = useGetVfdCmsParamsQuery(VFD_CMS_PARAMS_URL);
  const vfdsQ = useGetAhuVfdsQuery(ahuId ?? 0, { skip: ahuId == null });
  const sectionsQ = useGetAhuSectionsQuery(ahuId ?? 0, { skip: ahuId == null });
  const [fetchByMotor] = useLazyGetVfdByMotorQuery();
  const [fetchCmsCount] = useLazyGetVfdCmsCountQuery();
  const [fetchCmsParams] = useLazyGetVfdCmsParamsQuery();
  const [fetchQtyTotal] = useLazyGetVfdQtyTotalQuery();
  const [fetchArrangements] = useLazyGetVfdMountingArrangementsQuery();
  const [fetchConfigurations] = useLazyGetVfdConfigurationsQuery();
  const [fetchPosition] = useLazyGetAccessoryPositionQuery();
  const [postComputePosition] = usePostVfdComputePositionMutation();

  // local view state — refs + bump (the FanSelector state pattern).
  const [, setLocalVersion] = useState(0);
  const localBump = useCallback(() => setLocalVersion((n) => n + 1), []);

  // legacy self._vfdSmartlist (js:634-643).
  const sl = useRef<SmartlistState>({
    data: [], count: null, cmsCount: null, isLoading: false,
    reqId: 0, isOpen: false, page: 1, hasMore: false,
  });
  const slTimer = useRef<number | null>(null);
  // legacy self._vfdQtyReqId (stale-request guard js:8571).
  const qtyReqId = useRef(0);
  // Dev-sync H — legacy self._vfdConfigurations + self._vfdConfigReqId
  // (loadVfdConfigurations on merged dev). null = never loaded yet → the
  // select briefly renders the FULL vfdCms list, exactly like the legacy tmpl
  // render before the first loadVfdConfigurations pass replaces the options.
  const vfdConfigsRef = useRef<VfdCmsRow[] | null>(null);
  const vfdConfigReqId = useRef(0);
  const arrangementsRef = useRef<VfdMountingArrangement[]>([]);
  // legacy self._filteredVfdModels (voltage-filtered Model options, js:8525).
  const filteredModelsRef = useRef<VfdCmsRow[] | null>(null);
  // Dev-sync I — legacy self._filteredVfdManufacturers/_filteredVfdSeries
  // (_refreshVfdMfgSeriesDropdowns, fan.selector.js:8994-9028 on merged dev):
  // the Manufacturer/Series option lists narrowed to the current motor via the
  // MasterAHUMtrVfgSeriesVFDMfgSeries mapping. null = never filtered → the
  // full vfdCms lists render, exactly like the legacy tmpl pre-filter render.
  const filteredMfgSeriesRef = useRef<{
    manufacturer: VfdCmsRow[] | null;
    series: VfdCmsRow[] | null;
  }>({ manufacturer: null, series: null });
  // formatter.js `invalid` class survives the clamp (formatter.js 75-99).
  const spacingInvalid = useRef({ horizontal: false, vertical: false });
  // enc dims below their dynamic minimum — same formatter.js pattern.
  const encInvalid = useRef({ enc_width: false, enc_height: false });
  // stale-response guard for compute_position (the qty-chain reqId pattern).
  const computeReqId = useRef(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const headingRef = useRef<HTMLUListElement | null>(null);
  const [initedFor, setInitedFor] = useState<number | null>(null);

  // ---- helpers ----------------------------------------------------------

  const dash = '- -';
  // applyVFDPositionResponse's fmt (js:8433-8437) — toFixed(2); B1's '- -'
  // placeholder stands in for the legacy empty string.
  const fmt2 = (val: unknown): string => {
    if (val == null || val === '') return dash;
    const n = Number(val);
    return Number.isFinite(n) ? n.toFixed(2) : String(val);
  };
  // toggleInvalidIfNegative (js:4361-4382): class `warning` (the legacy CSS
  // reds it — distinct from `invalid`) when the shown value is a number < 0.
  const negWarn = (val: unknown): boolean => {
    const raw = String(val ?? '').replace(/,/g, '').trim();
    return raw !== '' && !Number.isNaN(Number(raw)) && Number(raw) < 0;
  };

  // _resolveVfdConfigurationValue (js:8328-8337). Dev-sync H: prefer the list
  // loaded by loadVfdConfigurations (/vfds/configurations/, mfg/series-
  // filtered) — fall back to vfdCms for safety (fan.selector.js:9249-9262 on
  // merged dev).
  const resolveConfigurationValue = (id: unknown): string | null => {
    if (isVfdNone(id)) return null;
    let configurations: VfdCmsRow[] = vfdConfigsRef.current ?? [];
    if (!configurations.length) configurations = vfdCms?.vfd?.configuration ?? [];
    const c = configurations.find((row) => String(row.id) === String(id));
    if (c?.value != null) return String(c.value);
    // Dev-sync I (fan.selector.js:9549-9556 on merged dev) — neither list has
    // loaded/matched yet (e.g. the very first smartlist call, before vfdCms and
    // the configurations resolve): fall back to the well-known default so
    // by_motor still gets a `configuration` and applies its required_fla sizing
    // floor immediately, instead of silently omitting it and letting an
    // undersized VFD show up in the smartlist.
    return String(id) === String(DEFAULT_VFD_CONFIGURATION_ID)
      ? DEFAULT_VFD_CONFIGURATION_VALUE
      : null;
  };

  // Dev-sync I — legacy _currentMtrMfgSeries (fan.selector.js:8887-8896 on
  // merged dev): the motor mfg/series currently selected (or, before the user
  // has touched the Motor tab, whatever the persisted fan already has) —
  // drives both the by_motor smartlist and the Mfg/Series dropdown filtering.
  const currentMtrMfgSeries = (): {
    mtr_mfg_id: number | null;
    mtr_series_id: number | null;
  } => {
    const s = state.current;
    return {
      mtr_mfg_id:
        (selectedMotor?.mfg_id as number | undefined) ??
        (s.motor_mfg_id as number | undefined) ??
        null,
      mtr_series_id:
        (selectedMotor?.series_id as number | undefined) ??
        (s.motor_series_id as number | undefined) ??
        null,
    };
  };

  // The FLA of the motor this VFD has to drive — the floor every VFD offered
  // for it must clear (`fla >= required_fla`, applied by both by_motor and the
  // Model-dropdown params call). `selectedMotor` is `motor_choosen ||
  // selectedMotorData`, so this is the row the user picked on the Motor tab
  // before the fan is Selected and the saved one afterwards. Omitting it is NOT
  // harmless: the backend then applies no floor at all and the smartlist offers
  // drives smaller than the motor (reported 2026-10-03 — an Annexair/Domel
  // 8.00 A motor was offered the 7.2 A OJ AX-DV-3030-TGN7, which then could not
  // show in the Model dropdown because that call DID carry the floor).
  const currentMotorFla = (): number | string | null =>
    (selectedMotor?.fla as number | string | undefined) ??
    (state.current.motor_fla as number | string | undefined) ??
    null;

  // _applyVfdModelFilter (js:8530-8548): a stored model missing from the
  // voltage-filtered list displays as "None" (the legacy code forces the DOM
  // select to "None" without touching vfd_params.model); the smartlist QS uses
  // the same effective value so filter and display stay consistent.
  const effectiveVfdModel = (): string | null => {
    const m = paramsRef.current.model;
    if (isVfdNone(m)) return null;
    const models = filteredModelsRef.current;
    if (models && !models.some((row) => String(row.model) === String(m))) return null;
    return String(m);
  };

  // _refreshVfdModelDropdown — dev-sync I rewrite (fan.selector.js:9861-9928
  // on merged dev, the two "VFD: Fix model filtering issue" commits): the URL
  // is built from scratch with the CURRENT motor mfg/series (the same source
  // the Mfg/Series dropdowns use — a stale urlRoot-style context combined with
  // a fresh mfg/series pick is self-contradictory and silently returned zero
  // models), scoped to the selected VFD Manufacturer/Series, and carrying the
  // same required-FLA sizing params the smartlist (by_motor) sends
  // (motor_fla/configuration/fan_qty) — without them the Model dropdown was
  // offering models the smartlist would never return.
  const refreshVfdModelDropdown = (voltageId: number | string) => {
    const { mtr_mfg_id, mtr_series_id } = currentMtrMfgSeries();
    const p = paramsRef.current;
    const params = [`voltage_id=${encodeURIComponent(String(voltageId))}`];
    if (mtr_mfg_id) params.push(`mtr_mfg_id=${encodeURIComponent(String(mtr_mfg_id))}`);
    if (mtr_series_id != null) params.push(`mtr_series_id=${encodeURIComponent(String(mtr_series_id))}`);
    if (!isVfdNone(p.vfd_mfg_id)) params.push(`mfg_id=${encodeURIComponent(String(p.vfd_mfg_id))}`);
    if (!isVfdNone(p.vfd_series_id)) params.push(`series_id=${encodeURIComponent(String(p.vfd_series_id))}`);
    const motorFla = currentMotorFla();
    // Falls back to the same well-known default resolveConfigurationValue uses
    // (js:9903-9910) — configuration_id can still be genuinely unset here, and
    // omitting `configuration` entirely used to silently drop the required-FLA
    // floor instead of applying the loosest one.
    const configValue =
      (vfdRef.current && resolveConfigurationValue(vfdRef.current.configuration_id)) ||
      DEFAULT_VFD_CONFIGURATION_VALUE;
    if (motorFla != null) params.push(`motor_fla=${encodeURIComponent(String(motorFla))}`);
    if (configValue) params.push(`configuration=${encodeURIComponent(configValue)}`);
    if (fanQty) params.push(`fan_qty=${encodeURIComponent(String(fanQty))}`);
    void fetchCmsParams(`${VFD_CMS_PARAMS_URL}?${params.join('&')}`, true)
      .unwrap()
      .then((resp) => {
        filteredModelsRef.current = resp?.vfd?.model ?? [];
        localBump();
      })
      .catch(() => undefined);
  };

  // voltage is derived from the motor, never user-editable (js:8504-8517).
  const setVfdVoltageFromMotor = () => {
    const voltageId =
      (state.current.motor_voltage_id as number | undefined) ??
      (detail?.motor_voltage_id as number | undefined);
    if (!voltageId || !vfdRef.current) return;
    vfdRef.current.voltage_id = voltageId;
    paramsRef.current.voltage_id = voltageId;
    refreshVfdModelDropdown(voltageId);
  };

  // _vfdByMotorUrl (js:7866-7896) — null only while the vfd state hasn't been
  // initialized yet. Manual filters read the React state (the legacy live-DOM
  // sweep _buildVfdSmartlistQS js:7924-7942 — state is authoritative here).
  const byMotorUrl = (): string | null => {
    const v = vfdRef.current;
    if (!v) return null;
    const s = state.current;
    return buildVfdByMotorUrl(
      {
        motor_fla: currentMotorFla(),
        // The sizing floor is `fla >= required_fla(motor_fla, configuration,
        // fan_qty)`, and the backend drops it entirely when `configuration` is
        // missing — so fall back to the same well-known default the Model
        // dropdown already uses rather than letting an unresolved
        // configuration_id silently unfilter the whole smartlist.
        configuration:
          resolveConfigurationValue(v.configuration_id) || DEFAULT_VFD_CONFIGURATION_VALUE,
        // legacy sends the raw qty_wide*qty_high product; guard non-finite so
        // a transient NaN can't 400 the int() cast server-side.
        fan_qty: Number.isFinite(fanQty) && fanQty > 0 ? fanQty : null,
        // dev-sync I: the shared _currentMtrMfgSeries source (the legacy
        // _vfdByMotorUrl refactor, js:8950-8953 on merged dev — same values).
        ...currentMtrMfgSeries(),
        voltage_id: (s.motor_voltage_id as number | undefined) ?? (detail?.motor_voltage_id as number | undefined) ?? null,
      },
      {
        mfg_id: paramsRef.current.vfd_mfg_id ?? null,
        series_id: paramsRef.current.vfd_series_id ?? null,
        model: effectiveVfdModel(),
        rating: paramsRef.current.fla ?? null,
        description_id: paramsRef.current.description_id ?? null,
        voltage_id: v.voltage_id ?? null,
      }
    );
  };

  // ---- compute flow (computeVFDPosition js:8392-8409) --------------------

  // applyVFDPositionResponse (js:8412-8472): merge resp.vfd into the vfd
  // state and resp.position into the position state. The readonly outputs
  // (Bank W×H, Hy/Wx/Lx, position x/y echoes), the dynamic enc minimum
  // ((bank||0)+2*spacing, js:8462-8469) and the negative-distance `warning`
  // classes (js:4361-4382) are all render-derived from the merged state —
  // the legacy code fills the DOM + re-applies classes imperatively.
  const applyVfdPositionResponse = (resp: VfdComputeResponse | undefined) => {
    if (!resp) return;
    if (vfdRef.current) vfdRef.current = { ...vfdRef.current, ...(resp.vfd ?? {}) };
    posRef.current = { ...posRef.current, ...(resp.position ?? {}) };
    bump();
  };

  // POST /api/v1.0/ahu/vfds/compute_position — a STATELESS preview (the
  // backend builds transient models, saves nothing). No-op without vfd state
  // or a section_id (js:8395-8397).
  const computeVFDPosition = () => {
    const v = vfdRef.current;
    if (!v) return;
    const payload = buildVfdPayload(v, posRef.current, paramsRef.current, fanComponentId);
    if (!payload.vfd.section_id) return;
    const reqId = ++computeReqId.current;
    void postComputePosition(payload)
      .unwrap()
      .then((resp) => {
        if (reqId !== computeReqId.current) return; // stale — a newer call is in progress
        applyVfdPositionResponse(resp);
      })
      .catch((err) => console.error('VFD position computation failed', err));
  };

  // ---- qty chain (updateVfdQtyTotal js:8554-8594) ------------------------

  const loadVfdMountingArrangements = (qtyTotal: number | null, reqId: number) => {
    if (qtyTotal == null) return;
    void fetchArrangements(buildVfdMountingArrangementsUrl(qtyTotal), true)
      .unwrap()
      .then((arrangements) => {
        if (reqId !== qtyReqId.current) return; // stale — a newer call is in progress
        arrangementsRef.current = arrangements ?? [];
        const v = vfdRef.current;
        if (v && (!v.mounting_arrangement_id ||
          !arrangementsRef.current.find((a) => a.id == v.mounting_arrangement_id))) {
          v.mounting_arrangement_id = arrangementsRef.current[0]?.id ?? null;
          v.enc_width = null;
          v.enc_height = null;
        }
        computeVFDPosition(); // js:8643 — refresh bank/position outputs
        bump();
      })
      .catch(() => undefined);
  };

  const updateVfdQtyTotal = (init = false) => {
    if (!init && !motorSupportsVfd) return;
    if (!(init || selectedMotor)) return;
    const v = vfdRef.current;
    if (!v) return;
    const configValue = resolveConfigurationValue(v.configuration_id);
    if (!configValue) return;
    const qty =
      (Number(state.current.qty_wide) || 0) * (Number(state.current.qty_high) || 0) ||
      Number(detail?.fan_qty) || 1;
    const reqId = ++qtyReqId.current;
    void fetchQtyTotal(buildVfdQtyTotalUrl(configValue, qty), true)
      .unwrap()
      .then((resp) => {
        if (reqId !== qtyReqId.current) return; // stale — a newer call is in progress
        qtyTotalRef.current = resp?.qty_total ?? null;
        // FanSelector-level bump: the VFD OUTPUT tab's Quantity (B4) reads
        // this ref, and the arrangements-load bump below is skipped when
        // qty_total comes back null (loadVfdMountingArrangements early-out).
        bump();
        loadVfdMountingArrangements(qtyTotalRef.current, reqId);
      })
      .catch((err) => console.error('VFD qty_total computation failed', err));
  };

  // ---- Dev-sync H — VFD-to-Motor Configuration filter chain ----------------
  // (fan.selector.js loadVfdConfigurations on merged dev.)

  // the `$select.trigger("change")` tail for #configuration_id — i.e. the
  // changeVfdField semantics for that select (set/clear the value, blank the
  // enc dims, re-run the qty chain). Mirrors onModelSelectChange minus bump
  // (the caller bumps once).
  const applyConfigurationChange = (id: number | null) => {
    const v = vfdRef.current;
    if (!v) return;
    v.configuration_id = id;
    v.enc_height = null;
    v.enc_width = null;
    updateVfdQtyTotal();
  };

  // Populates the VFD-to-Motor Configuration dropdown from
  // MasterAHUVFDConfiguration via /vfds/configurations/. Stays EMPTY (just the
  // "- -" placeholder) until BOTH the VFD Mfg and Series filters are selected —
  // only then is the API called, filtered server-side (via
  // MasterAHUVFDMfgSeriesVFDConfiguration) to that mfg/series pair. Re-run
  // whenever those filters change so the dropdown stays in sync.
  const loadVfdConfigurations = () => {
    const mfgId = paramsRef.current.vfd_mfg_id;
    const seriesId = paramsRef.current.vfd_series_id;
    // Bump the req id even on the early-out below so any in-flight request
    // from a previous mfg/series pick is ignored once it lands.
    const reqId = ++vfdConfigReqId.current;
    const v = vfdRef.current;
    if (isVfdNone(mfgId) || isVfdNone(seriesId)) {
      vfdConfigsRef.current = [];
      // Dev-sync I (fan.selector.js:9742-9790 on merged dev) — no Mfg/Series
      // chosen yet, so there's no mfg/series-filtered list to populate the
      // dropdown from. Rather than blanking it to "- -" (which used to also
      // wipe configuration_id back to null, undoing the "One to One" default a
      // fresh VFD is initialized with — DEFAULT_VFD_CONFIGURATION_ID), show
      // "One to One" pre-selected as long as the user hasn't explicitly picked
      // a different configuration already. A previously-chosen non-default
      // configuration still gets cleared below, since it no longer has a
      // mfg/series context to belong to.
      const current = v ? v.configuration_id : null;
      if (isVfdNone(current) || String(current) === String(DEFAULT_VFD_CONFIGURATION_ID)) {
        const allConfigurations = vfdCms?.vfd?.configuration ?? [];
        const defaultConfig = allConfigurations.find(
          (c) => String(c.id) === String(DEFAULT_VFD_CONFIGURATION_ID)
        );
        // the dropdown offers exactly the default option (legacy re-appends it
        // to the emptied select, js:9770-9784).
        vfdConfigsRef.current = defaultConfig ? [defaultConfig] : [];
        if (v) v.configuration_id = DEFAULT_VFD_CONFIGURATION_ID;
        bump();
        return;
      }
      applyConfigurationChange(null); // the `$select.trigger("change")` tail (js:9791-9795)
      bump();
      return;
    }
    void fetchConfigurations(buildVfdConfigurationsUrl(mfgId as number, seriesId as number))
      .unwrap()
      .then((configurations) => {
        if (reqId !== vfdConfigReqId.current) return; // stale
        vfdConfigsRef.current = configurations ?? [];
        const selected =
          v && !isVfdNone(v.configuration_id) ? String(v.configuration_id) : null;
        const stillValid =
          selected != null &&
          vfdConfigsRef.current.some((c) => String(c.id) === selected);
        if (stillValid) {
          // keep the selection — nothing downstream changes.
        } else if (vfdConfigsRef.current.length === 1) {
          // Exactly one configuration matches this mfg/series — auto-select it
          // (same "only one option" convenience as the smartlist's own
          // auto-pick) and let it cascade downstream via the change semantics.
          applyConfigurationChange(Number(vfdConfigsRef.current[0].id));
        } else if (selected != null) {
          // Previously chosen configuration no longer matches this mfg/series
          // — clear it downstream too (configuration_id, enc dims, Quantity),
          // same as a manual reset.
          applyConfigurationChange(null);
        }
        // _resolveVfdConfigurationValue reads this list — re-run the qty total
        // now that it's populated, in case the initial _getVFD() pass (which
        // fires before this async load resolves) silently skipped it for lack
        // of a resolvable configuration value.
        updateVfdQtyTotal();
        bump();
      })
      .catch((err) => console.error('VFD configuration list load failed', err));
  };

  // ---- smartlist (loadVfdSmartlist js:7974-8054, debounced 300ms) ---------

  const selectFromVfdSmartlist = (item: VfdByMotorItem, keepOpen: boolean) => {
    const v = vfdRef.current;
    if (!v || !item) return;
    v.master_vfd_id = item.id;
    v.voltage_id = item.voltage_id ?? null;
    // Keys stay vfd_-prefixed to match the left-column selects + the smartlist
    // filter builder (js:8250-8267 — the selectFromVfdSmartlist fix).
    paramsRef.current = {
      ...paramsRef.current,
      ...item,
      vfd_mfg_id: item.mfg_id,
      vfd_series_id: item.series_id,
      mfg_id: item.mfg_id,
      series_id: item.series_id,
      model: item.model,
      fla: item.fla,
      fla_input: item.fla_input,
      description_id: item.description_id,
      voltage_id: item.voltage_id,
    };
    // Dev-sync H: the smartlist pick sets vfd_mfg_id/vfd_series_id directly
    // (no filter-change event fires), so run the configuration filter here —
    // legacy calls loadVfdConfigurations() explicitly in this handler
    // (fan.selector.js:9179-9188 on merged dev) so VFD-to-Motor Configuration
    // filters to this item's mfg/series right away instead of staying empty.
    loadVfdConfigurations();
    // …and, for the same reason, re-scope the Model dropdown to the picked
    // mfg/series. Its option list is fetched per Manufacturer/Series, so a pick
    // that changes either (a row from a mfg the list isn't scoped to, or one
    // reached after resetting the filters) left Model showing "- -" because the
    // model that was just picked wasn't among the options — the symptom Anna
    // reported on 2026-10-03.
    if (v.voltage_id != null) refreshVfdModelDropdown(v.voltage_id);
    // updateVfdPositionFieldsState is render-derived; the checkButtonsAvailability
    // vfd-tab-invalid class + the Select gate live in FanSelector (isVfdValidNow).
    computeVFDPosition(); // js:8269
    const s = sl.current;
    // the badge keeps the prior filtered count — legacy selectFromVfdSmartlist
    // (js:8225-8283) never touches it; only the next loadVfdSmartlist does.
    // auto-pick (count===1) keeps the panel open; only a row click closes it.
    if (!keepOpen) s.isOpen = false;
    bump();
  };

  const loadVfdSmartlist = () => {
    if (!motorSupportsVfd) return;
    if (slTimer.current) window.clearTimeout(slTimer.current);
    slTimer.current = window.setTimeout(() => {
      const url = byMotorUrl();
      if (!url) return;
      const s = sl.current;
      s.isLoading = true;
      const reqId = ++s.reqId;
      localBump();
      void Promise.all([
        fetchByMotor(url, true).unwrap(),
        fetchCmsCount(undefined, true).unwrap(),
      ])
        .then(([resp, totalData]) => {
          if (reqId !== s.reqId) return; // stale — a newer call is in progress
          s.data = resp?.results ?? [];
          s.count = resp?.filtered_count != null ? resp.filtered_count : s.data.length;
          s.cmsCount = totalData?.count ?? null;
          s.page = 1;
          s.hasMore = s.data.length < (s.count ?? 0);
          s.isLoading = false;
          localBump();
          // Auto-pick: exactly one match — select it automatically.
          // Guarded by s.isOpen so that narrowing a filter dropdown with the
          // panel closed never silently overwrites an already-chosen VFD — but
          // when nothing is selected yet (fresh fan, no saved master_vfd_id)
          // there is nothing to protect, so auto-pick even with the panel
          // closed instead of forcing the user to open it.
          // dev-sync G / UF-3 (fan.selector.js:8303-8313; legacy `isNull`
          // :9268-9270 is byte-for-byte isVfdNone).
          if (
            s.count === 1 &&
            s.data.length === 1 &&
            (s.isOpen || isVfdNone(vfdRef.current?.master_vfd_id))
          ) {
            selectFromVfdSmartlist(s.data[0], true);
          }
        })
        .catch(() => {
          if (reqId !== s.reqId) return;
          s.isLoading = false;
          localBump();
        });
    }, 300);
  };

  // loadMoreVfdSmartlist (js:8060-8105) — infinite scroll, 50/page.
  const loadMoreVfdSmartlist = () => {
    const s = sl.current;
    if (s.isLoading || !s.hasMore) return;
    const base = byMotorUrl();
    if (!base) return;
    const nextPage = s.page + 1;
    const pageUrl = `${base}${base.indexOf('?') === -1 ? '?' : '&'}page=${nextPage}`;
    s.isLoading = true;
    localBump();
    void fetchByMotor(pageUrl, true)
      .unwrap()
      .then((resp) => {
        const newItems = resp?.results ?? [];
        s.isLoading = false;
        if (!newItems.length) {
          s.hasMore = false;
          localBump();
          return;
        }
        s.page = nextPage;
        s.data = s.data.concat(newItems);
        s.count = resp?.filtered_count != null ? resp.filtered_count : s.count;
        s.hasMore = s.data.length < (s.count ?? 0);
        localBump();
      })
      .catch(() => {
        s.isLoading = false;
        localBump();
      });
  };

  // showVfdSmartlist (js:7949-7962) — flip the panel front ↔ smartlist.
  const toggleVfdSmartlist = () => {
    const s = sl.current;
    s.isOpen = !s.isOpen;
    localBump();
    if (s.isOpen) {
      window.setTimeout(() => listRef.current?.focus(), 0);
      loadVfdSmartlist();
    }
  };

  // resetVfdSmartlistParams (js:7964-7972). The legacy handler only blanks the
  // DOM selects (the QS is DOM-swept); React state is authoritative, so the
  // filter keys are cleared here instead.
  const resetVfdSmartlist = () => {
    const p = paramsRef.current;
    p.vfd_mfg_id = null;
    p.vfd_series_id = null;
    p.model = null;
    p.fla = null;
    p.description_id = null;
    if (vfdRef.current) vfdRef.current.master_vfd_id = null;
    loadVfdSmartlist();
    // Dev-sync H: the legacy reset fires a real "change" on every
    // .vfd_smartlist_field (`.val("None").trigger("change")`), which runs
    // changeVfdSmartlistField → loadVfdConfigurations — mfg/series are now
    // empty, so the configuration dropdown empties and any selection clears.
    loadVfdConfigurations();
    computeVFDPosition(); // js:7969
    bump();
  };

  const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (headingRef.current) headingRef.current.scrollLeft = el.scrollLeft;
    if (el.scrollHeight - (el.scrollTop + el.clientHeight) < 100) loadMoreVfdSmartlist();
  };

  // ---- Dev-sync I — motor→VFD Manufacturer/Series filtering ----------------
  // (_refreshVfdMfgSeriesDropdowns + _applyVfdMfgSeriesFilter,
  // fan.selector.js:8990-9067 on merged dev, "VFD: Fix mfg series filtering
  // issue" + "Annexair Fan: Fixes".)

  // _applyVfdMfgSeriesFilter (js:9034-9067): keep a still-offered selection;
  // auto-select a single mapped option (the legacy triggers a REAL change for
  // that case only — the changeVfdSmartlistField cascade); a selection the
  // narrowed list no longer offers falls back to "- -" WITHOUT a change event
  // (legacy `$select.val("None")` only), i.e. no smartlist/config reload.
  const applyVfdMfgSeriesFilter = () => {
    const p = paramsRef.current;
    let autoSelected = false;
    let mutated = false;
    (['vfd_mfg_id', 'vfd_series_id'] as const).forEach((key) => {
      const options =
        key === 'vfd_mfg_id'
          ? filteredMfgSeriesRef.current.manufacturer
          : filteredMfgSeriesRef.current.series;
      if (!options) return;
      const current = p[key];
      const stillValid =
        !isVfdNone(current) && options.some((o) => String(o.id) === String(current));
      if (stillValid) return;
      if (options.length === 1) {
        // Exactly one option mapped for this motor — auto-select it (same
        // "only one option" convenience as the VFD Configuration dropdown)
        // and cascade downstream via the change semantics below.
        p[key] = Number(options[0].id);
        autoSelected = true;
      } else if (!isVfdNone(current)) {
        p[key] = null;
        mutated = true;
      }
    });
    if (autoSelected) {
      // the `$select.trigger("change")` tail → changeVfdSmartlistField
      // (clear the picked VFD, re-derive voltage, reload smartlist + configs).
      const v = vfdRef.current;
      if (v) {
        v.master_vfd_id = null;
        v.voltage_id = null;
      }
      setVfdVoltageFromMotor();
      loadVfdSmartlist();
      loadVfdConfigurations();
    }
    if (autoSelected || mutated) bump();
    else localBump(); // new option lists only — re-render the selects
  };

  // _refreshVfdMfgSeriesDropdowns (js:8994-9028): re-filter the Manufacturer/
  // Series dropdowns to the motor now selected (MasterAHUMtrVfgSeriesVFDMfgSeries
  // mapping, applied server-side by /vfd/params/). The currently-selected combo
  // is passed through (selected_*) so re-filtering on a motor change doesn't
  // blank out a VFD the mapping table hasn't caught up to; fan_id additionally
  // lets the backend keep the fan's PERSISTED VFD combo by looking it up
  // directly from the DB (the legacy fanId fallback, fn/map.js on merged dev).
  const refreshVfdMfgSeriesDropdowns = () => {
    if (!vfdCms) return;
    const { mtr_mfg_id, mtr_series_id } = currentMtrMfgSeries();
    if (!mtr_mfg_id) return; // no motor context → keep the full lists
    const p = paramsRef.current;
    const qs = [`mtr_mfg_id=${encodeURIComponent(String(mtr_mfg_id))}`];
    if (mtr_series_id != null) qs.push(`mtr_series_id=${encodeURIComponent(String(mtr_series_id))}`);
    if (!isVfdNone(p.vfd_mfg_id)) qs.push(`selected_mfg_id=${encodeURIComponent(String(p.vfd_mfg_id))}`);
    if (!isVfdNone(p.vfd_series_id)) qs.push(`selected_series_id=${encodeURIComponent(String(p.vfd_series_id))}`);
    if (fanComponentId) qs.push(`fan_id=${encodeURIComponent(String(fanComponentId))}`);
    void fetchCmsParams(`${VFD_CMS_PARAMS_URL}?${qs.join('&')}`, true)
      .unwrap()
      .then((resp) => {
        filteredMfgSeriesRef.current = {
          manufacturer: resp?.vfd?.manufacturer ?? [],
          series: resp?.vfd?.series ?? [],
        };
        applyVfdMfgSeriesFilter();
      })
      .catch(() => undefined);
  };

  // ---- field changes (changeVfdField js:4387-4467) ------------------------

  // the 5 catalog-cascade selects (.vfd_params_field.vfd_smartlist_field):
  // any change clears the picked VFD + re-derives voltage + reloads the list.
  const onFilterChange = (name: string, raw: string) => {
    const cleared = raw === '' || raw === 'None';
    const p = paramsRef.current;
    if (name === 'model' || name === 'fla') p[name] = cleared ? null : raw;
    else p[name] = cleared ? null : Number(raw);
    const v = vfdRef.current;
    if (v) {
      v.master_vfd_id = null;
      v.voltage_id = null;
    }
    // Dev-sync I (fan.selector.js:4699-4717 on merged dev, "VFD: Fix model
    // filtering issue"): Model and Rating both narrow the by_motor lookup to
    // the same `fla` column server-side (exact match on Rating ANDed with an
    // exact match on Model). Neither select is cross-filtered by the other, so
    // a leftover value in one almost never matches the FLA of what was just
    // picked in the other and the AND silently zeroes the results — clear the
    // other one on ANY change of either (the legacy branch is unconditional).
    // (A smartlist row pick sets both together from the same catalog item, so
    // it isn't affected.)
    if (name === 'model') p.fla = null;
    else if (name === 'fla') p.model = null;
    setVfdVoltageFromMotor();
    loadVfdSmartlist();
    // Dev-sync H — changeVfdSmartlistField now also re-filters the
    // VFD-to-Motor Configuration dropdown (fan.selector.js:8813-8817 on
    // merged dev: `self.loadVfdSmartlist(); self.loadVfdConfigurations();`).
    loadVfdConfigurations();
    bump();
  };

  // .vfd_model_field selects → self.vfd (wiring/supplied_by/configuration/
  // section/mounting_arrangement).
  const onModelSelectChange = (name: string, raw: string) => {
    const v = vfdRef.current;
    if (!v) return;
    v[name] = raw === '' || raw === 'None' ? null : Number(raw);
    if (name === 'mounting_arrangement_id' || name === 'configuration_id') {
      v.enc_height = null;
      v.enc_width = null;
    }
    // mounting_arrangement_id / section_id recompute directly (js:4443-4451);
    // configuration_id goes through the qty chain instead — its arrangements
    // load ends in a compute (js:8643).
    if (name === 'mounting_arrangement_id' || name === 'section_id') computeVFDPosition();
    if (name === 'configuration_id') updateVfdQtyTotal();
    bump();
  };

  const onHousedChange = (checked: boolean) => {
    const v = vfdRef.current;
    if (!v) return;
    v.housed_in_enclosure = checked;
    // recess is only valid when housed — force Surface + gate the radio
    // (js:4421-4434); values of other fields are NOT cleared.
    v.casing_mount_id = 1;
    if (!checked) {
      v.enc_width = '';
      v.enc_height = '';
      encInvalid.current.enc_width = false;
      encInvalid.current.enc_height = false;
    }
    computeVFDPosition(); // js:4442
    bump();
  };

  const onPlaneChange = (raw: string) => {
    posRef.current.mounting_plane_id = raw === '' || raw === 'None' ? null : Number(raw);
    // wall-row visibility + the negative-distance `warning` classes are
    // render-derived (updateVfdMountingPlane js:4348-4359).
    computeVFDPosition(); // js:4444
    bump();
  };

  const onDefaultPosChange = (name: 'default_pos_x' | 'default_pos_y', checked: boolean) => {
    // check → paired input re-locks + recompute; uncheck → manual entry, no
    // compute (js:4453-4465).
    posRef.current[name] = checked;
    if (checked) computeVFDPosition();
    bump();
  };

  // Numeric commits take the input element: legacy formatter.js ALWAYS
  // re-encodes the DOM to the committed/clamped value (formatter.js 75-99) —
  // the keyed-remount trick alone misses the case where the clamp target
  // equals the prior state (raw text would stay stale forever).
  // "Changed" is judged at DISPLAY precision (2dp): the compute echo carries
  // >2dp state, and an untouched blur re-parses the 2dp display — legacy fires
  // no `change` event (and keeps full precision) without a real edit.
  const round2 = (val: unknown): number => Math.round(Number(val) * 100) / 100;

  const commitPos = (axis: 'x' | 'y', input: HTMLInputElement) => {
    const pos = posRef.current;
    const trimmed = input.value.replace(/,/g, '').trim();
    const n = Number(trimmed);
    if (trimmed === '') {
      pos[axis] = null;
      input.value = '';
      bump();
      return;
    }
    if (!Number.isFinite(n)) {
      // garbage → re-encode back to the committed value (fmt2 keeps '- -').
      input.value = fmt2(pos[axis]);
      bump();
      return;
    }
    const next = round2(n);
    if (pos[axis] == null || pos[axis] === '' || round2(pos[axis]) !== next) pos[axis] = next;
    input.value = next.toFixed(2);
    bump();
  };

  const commitSpacing = (which: 'horizontal' | 'vertical', input: HTMLInputElement) => {
    const v = vfdRef.current;
    if (!v) return;
    const trimmed = input.value.replace(/,/g, '').trim();
    const n = Number(trimmed);
    if (trimmed === '' || !Number.isFinite(n)) {
      spacingInvalid.current[which] = false;
      // spacing can't be cleared — restore the committed value.
      input.value = formatDecimalDisplay(v[`spacing_${which}`] ?? 2);
      bump();
      return;
    }
    // formatter.js semantics: flag `invalid` from the raw value, THEN clamp
    // into [2,12] (formatter.js 75-99, 182-195).
    spacingInvalid.current[which] = n < 2 || n > 12;
    const prev = v[`spacing_${which}`];
    const next = Math.min(12, Math.max(2, round2(n)));
    // recompute only on an actual change — the legacy `change` event doesn't
    // fire on an untouched blur (js:4446-4451).
    if (prev == null || round2(prev) !== next) {
      v[`spacing_${which}`] = next;
      computeVFDPosition();
    }
    input.value = formatDecimalDisplay(next);
    bump();
  };

  // dynamic minimum for the enc dims = (bank||0) + 2*spacing — the legacy
  // data-min set by applyVFDPositionResponse (js:8462-8469).
  const encMin = (which: 'enc_width' | 'enc_height'): number => {
    const v = vfdRef.current;
    const bank = Number(which === 'enc_width' ? v?.bank_width : v?.bank_height) || 0;
    const spacing =
      Number(which === 'enc_width' ? v?.spacing_horizontal : v?.spacing_vertical) || 0;
    return bank + 2 * spacing;
  };

  const commitEnc = (which: 'enc_width' | 'enc_height', input: HTMLInputElement) => {
    const v = vfdRef.current;
    if (!v) return;
    const trimmed = input.value.replace(/,/g, '').trim();
    if (trimmed === '') {
      encInvalid.current[which] = false;
      const changed = !isVfdNone(v[which]);
      v[which] = null;
      input.value = '';
      // enc_* are in the compute-trigger list (js:4443-4451).
      if (changed) computeVFDPosition();
      bump();
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      input.value = isVfdNone(v[which]) ? '' : fmt2(v[which]);
      bump();
      return;
    }
    // formatter.js semantics against the dynamic minimum: flag `invalid` from
    // the raw value, THEN clamp (the spacing-input pattern; no maximum).
    const min = encMin(which);
    encInvalid.current[which] = n < min;
    const prev = v[which];
    const next = Math.max(min, round2(n));
    // display-precision compare: keep the compute echo's full precision on an
    // untouched blur, and don't fire a spurious compute.
    if (isVfdNone(prev) || round2(prev) !== next) {
      v[which] = next;
      computeVFDPosition();
    }
    input.value = next.toFixed(2);
    bump();
  };

  // ---- state init (_getVFD js:8763-8834) ----------------------------------

  useEffect(() => {
    if (initedFor === fanComponentId) return;
    if (!detail || !vfdCms) return;
    // wait for the persisted-VFD list (unless there is no AHU or the GET failed).
    if (ahuId != null && vfdsQ.data == null && !vfdsQ.isError) return;
    const rows = (vfdsQ.data ?? []) as AhuVfdRow[];
    // Dev-sync I — match EITHER fan FK (vfd_viewset.py filter_fan_id on merged
    // dev: `Q(fan_id=value) | Q(fan_motor_id=value)`): AHUAccessoryVFD carries
    // two independent FKs to AHUFan and older rows may have only fan_id set.
    const existing =
      rows.find((r) => r.fan_motor_id === fanComponentId || r.fan_id === fanComponentId) ?? null;
    if (existing) {
      // A persisted VFD row was found for this fan -- such a row only ever
      // exists because "Has VFD" was checked and saved, so the checkbox must
      // reflect that on reopen (js:8763-8834 has-vfd-fix). Without this,
      // vfdRef/paramsRef below populate the VFD tab's fields correctly but
      // hasVfd can still be false (e.g. stale/legacy fan_params.has_vfd),
      // rendering the checkbox unchecked even though a real VFD is selected
      // -- and persistVfdAccessory reads this same hasVfd state on save, so
      // an untouched save would DELETE the real VFD out from under the user.
      if (!hasVfd) setHasVfd(true);
      vfdRef.current = { ...existing };
      // the position row loads async below — plane 5 is the legacy default.
      posRef.current = { mounting_plane_id: 5 };
      let parsed: Record<string, any> = {};
      try {
        parsed = existing.vfd_params ? JSON.parse(String(existing.vfd_params)) : {};
      } catch {
        parsed = {};
      }
      if (existing.fla != null) parsed.fla = existing.fla;
      if (existing.fla_input != null) parsed.fla_input = existing.fla_input;
      // Back-compat remap: pre-fix records persisted unprefixed catalog keys
      // (js:8783-8803).
      const legacyVfdParamKeys: Record<string, string> = {
        mfg_id: 'vfd_mfg_id',
        series_id: 'vfd_series_id',
        model: 'model',
        fla: 'fla',
        description_id: 'description_id',
        voltage_id: 'voltage_id',
      };
      for (const [legacyKey, prefixedKey] of Object.entries(legacyVfdParamKeys)) {
        if (parsed[prefixedKey] == null && parsed[legacyKey] != null) {
          parsed[prefixedKey] = parsed[legacyKey];
        }
      }
      paramsRef.current = parsed;
      if (vfdRef.current.voltage_id == null && parsed.voltage_id != null) {
        vfdRef.current.voltage_id = parsed.voltage_id;
      }
      // the persisted position row (legacy vfd.position from stateStorage) —
      // GET ahu/positions/<accessory id> (position keyed by accessory id).
      void fetchPosition(existing.id, true)
        .unwrap()
        .then((posRow) => {
          posRef.current = { ...posRef.current, ...(posRow as Record<string, unknown>) };
          // Backbone had the persisted position synchronously (stateStorage,
          // js:8770-8776) before the init qty chain's compute fired; here the
          // row lands async, so recompute with the real plane/x/y once it does.
          computeVFDPosition();
          bump();
        })
        .catch(() => undefined);
      setVfdVoltageFromMotor();
      if (detail.master_fan_id) updateVfdQtyTotal(true);
    } else {
      posRef.current = { mounting_plane_id: 5 };
      paramsRef.current = {};
      vfdRef.current = {
        configuration_id: 1,
        section_id: section?.id ?? null,
        supplied_by_id: 1,
        wiring_id: 2,
        casing_mount_id: 1,
        spacing_horizontal: 2,
        spacing_vertical: 2,
      };
      setVfdVoltageFromMotor();
      updateVfdQtyTotal(true);
    }
    // Dev-sync H — the init call (fan.selector.js:1505 on merged dev:
    // `self.loadVfdSections(); self.loadVfdConfigurations();`). Dev-sync I
    // reversed the no-mfg/series outcome: instead of emptying the dropdown and
    // clearing the fresh-VFD configuration_id=1 default, the "One to One"
    // default now stays pre-selected until a real mfg/series context exists
    // (see the loadVfdConfigurations null-branch).
    loadVfdConfigurations();
    // Dev-sync I — the initial render()-time current.cms.vfd fetch now carries
    // the motor context + the persisted VFD combo (fn/map.js current.cms.vfd +
    // fan.selector.js:1330-1346 on merged dev): narrow Manufacturer/Series to
    // the motor right away. paramsRef is seeded above, so the persisted
    // vfd_mfg/vfd_series ride along as selected_*.
    refreshVfdMfgSeriesDropdowns();
    setInitedFor(fanComponentId);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, vfdCms, vfdsQ.data, vfdsQ.isError, ahuId, fanComponentId, initedFor]);

  // prefetchVfdByMotor (js:7898-7918): once the motor resolves, refresh the
  // voltage + smartlist badge + qty chain. The legacy `self.header` early-out
  // is N/A (no header host in the React port); the tab-non-clickable skip
  // covers the same "don't prefetch a dead tab" intent.
  useEffect(() => {
    if (initedFor !== fanComponentId) return;
    if (!selectedMotor || !motorSupportsVfd || tabDisabled) return;
    setVfdVoltageFromMotor();
    // Dev-sync I — prefetchVfdByMotor's request.then now also re-filters the
    // Manufacturer/Series dropdowns to the resolved motor
    // (fan.selector.js:8987-8992 on merged dev).
    refreshVfdMfgSeriesDropdowns();
    loadVfdSmartlist();
    updateVfdQtyTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMotor, motorSupportsVfd, tabDisabled, initedFor, fanComponentId]);

  // qty_wide/qty_high changes re-run the qty chain (changeFanField js:4541-4544).
  useEffect(() => {
    if (initedFor !== fanComponentId) return;
    updateVfdQtyTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fanQty]);

  // opening the VFD tab loads the smartlist once (activateInputLabel js:1655-1661).
  useEffect(() => {
    if (!isActive) return;
    if (sl.current.count == null) loadVfdSmartlist();
    // …and fetch the Model list if no scoped response has landed on THIS
    // instance yet. `filteredModelsRef` is instance-local, so a dialog that
    // remounted while a refresh was in flight (the response fills the previous
    // instance's ref) renders the UNFILTERED vfdCms list — every model of every
    // VFD manufacturer — until something else happens to refresh it.
    if (filteredModelsRef.current == null) setVfdVoltageFromMotor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // loadVfdSections' default selection (js:8487-8497): vfd.section_id || the
  // fan's own section. Init already seeds the fan's section for a new VFD;
  // this covers a persisted VFD whose section_id is null.
  useEffect(() => {
    if (initedFor !== fanComponentId) return;
    const v = vfdRef.current;
    if (!v || !isVfdNone(v.section_id) || section?.id == null) return;
    v.section_id = section.id;
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initedFor, fanComponentId, sectionsQ.data]);

  // Backbone recomputes when the fan's section length changes while the
  // selector is open (c_changed:length listener, js:1552-1560). The React
  // equivalent is the `section:length:changed` bus event (TagStrip emits it
  // on a length save); it carries no section id, so ANY length change
  // recomputes — a stateless superset of the legacy per-section listener.
  useEffect(() => {
    if (initedFor !== fanComponentId) return undefined;
    return ahuBoardBus.on('section:length:changed', () => computeVFDPosition());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initedFor, fanComponentId]);

  // EC/PMAC motors can't take a VFD — auto-uncheck + (render-derived) disable
  // (changeParams js:4649-4655; setDefaults can flip motor_type_id on an
  // mfg_id change, so this watches the derived flag, not the field edit).
  useEffect(() => {
    if (!motorSupportsVfd && hasVfd) setHasVfd(false);
  }, [motorSupportsVfd, hasVfd, setHasVfd]);

  useEffect(
    () => () => {
      if (slTimer.current) window.clearTimeout(slTimer.current);
    },
    []
  );

  // ---- render -------------------------------------------------------------

  const v = vfdRef.current;
  const pos = posRef.current;
  const p = paramsRef.current;
  const doc = vfdCms?.vfd ?? {};
  const s = sl.current;

  // updateVfdFieldsEnabledState (js:8660-8681): !has_vfd disables every
  // select/input except #has_vfd + marks the left-column wrappers and the
  // smartlist row non-clickable. Values are NOT cleared.
  const fieldsDisabled = !hasVfd;
  const leftColClass = `col-xs-12 left-column${fieldsDisabled ? ' non-clickable' : ''}`;
  // updateVfdPositionFieldsState (js:8288-8296): mounting/casing/spacing stay
  // disabled until the vfd state exists (always true once _getVFD ran).
  const positionDisabled = fieldsDisabled || !v;

  const isWallLeftRight = Number(pos.mounting_plane_id) == 3 || Number(pos.mounting_plane_id) == 4;
  const housed = Boolean(v?.housed_in_enclosure);
  const autoX = pos.default_pos_x !== false;
  const autoY = pos.default_pos_y !== false;
  const effModel = effectiveVfdModel();
  const modelRows = filteredModelsRef.current ?? doc.model ?? [];
  // ONE option per DISTINCT model name. The catalog rows are distinct ROWS, not
  // distinct models — the same model repeats across voltages, NEMA enclosure
  // types and descriptions — while this select is keyed AND valued by the model
  // STRING. Those duplicate React keys made the option list un-reconcilable:
  // when a narrower list arrived (a Manufacturer/Series pick, or the scoped
  // refresh replacing the full vfdCms fallback), React kept the previous list's
  // <option> elements in the DOM instead of replacing them, so the dropdown went
  // on offering models of other manufacturers and series — even though the list
  // in `filteredModelsRef` was already correctly scoped (reported 2026-10-05).
  const modelOptions = (() => {
    const seen = new Set<string>();
    const out: Array<{ key: string; value: string; label: string }> = [];
    modelRows.forEach((row) => {
      const model = String(row.model ?? '');
      if (!model || seen.has(model)) return;
      seen.add(model);
      out.push({ key: model, value: model, label: model });
    });
    return out;
  })();
  // Dev-sync I — the motor-filtered Manufacturer/Series lists (see
  // refreshVfdMfgSeriesDropdowns); the full vfdCms lists until a filter ran.
  const mfgRows = filteredMfgSeriesRef.current.manufacturer ?? doc.manufacturer ?? [];
  const seriesRows = filteredMfgSeriesRef.current.series ?? doc.series ?? [];
  // Dev-sync I — isOjVfdSelected (fan.selector.js:4606-4629 on merged dev):
  // OJ VFDs are motor-integrated (only ever paired with Domel motors), so the
  // physical panel-mounting rows don't apply. Checked by NAME — the legacy
  // reads the selected option's text — not a hardcoded pk.
  const isOjVfd =
    String(mfgRows.find((row) => String(row.id) === String(p.vfd_mfg_id))?.name ?? '')
      .trim()
      .toUpperCase() === 'OJ';
  // fan.selector.tmpl:1366 on merged dev ("Model view: Mounting Arrangement
  // fix"): the Mounting Arrangement row is ALSO hidden for Annexair fans
  // (params_fan.mfg_id == 237). Live state stands in for the render-time
  // Handlebars check.
  const isAnnexairFan = Number(state.current.mfg_id) == 237;
  // fix(fan-fixes 2378395584, "hide VFD Mounting tab for Annexair fans in
  // databar"): the whole physical-mounting section (form.tmpl's
  // mounting-section-wrapper, wrapped there in `{{#unless
  // model.skip_3d_model}}`) doesn't apply either — Annexair's VFD is built
  // into the motor housing, there's no separate box to position/mount. Every
  // vfd-oj-mount-row below shares this same hide gate as isOjVfd.
  const ojHide = isOjVfd || isAnnexairFan ? ' hide' : '';
  const voltageName =
    (doc.voltage ?? []).find((row) => String(row.id) === String(v?.voltage_id))?.name ?? '';

  // loadVfdSections (js:8476-8502): the AHU's sections; the fan's own section
  // stands in while the GET is in flight so the select doesn't blank out.
  const sectionRows: AhuSectionRow[] =
    sectionsQ.data ??
    (section?.id != null
      ? [{ id: section.id, label: section.label ?? section.tag ?? String(section.id) }]
      : []);

  // a select value + a fallback <option> for a stored value the option list
  // doesn't carry (the FanSelector select pattern).
  const selVal = (val: unknown): string => (isVfdNone(val) ? 'None' : String(val));

  const filterSelect = (
    name: string,
    id: string,
    dataName: string,
    value: unknown,
    options: Array<{ key: string; value: string; label: string }>
  ) => {
    const valStr = selVal(value);
    const hasRow = options.some((o) => o.value === valStr);
    return (
      <select
        name={name}
        id={id}
        data-name={dataName}
        className="vfd_params_field vfd_smartlist_field"
        disabled={fieldsDisabled}
        value={valStr}
        onChange={(e) => onFilterChange(name, e.target.value)}
      >
        <option value="None">- -</option>
        {options.map((o) => (
          <option key={o.key} value={o.value}>{o.label}</option>
        ))}
        {valStr !== 'None' && !hasRow && <option value={valStr}>{valStr}</option>}
      </select>
    );
  };

  return (
    <>
      {/* ===== left column (tmpl 1150-1292) ===== */}
      <div className="col-lg-6 col-xs-12" style={{ height: '100%', float: 'left' }}>
        <div className="col-xs-12 left-column">
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="has_vfd">Has VFD</span>
            </div>
          </div>
          <div className="col-xs-7">
            <input
              type="checkbox"
              name="has_vfd"
              id="has_vfd"
              className="params_fan"
              checked={hasVfd}
              onChange={(e) => setHasVfd(e.target.checked)}
            />
          </div>
        </div>

        <div className={`row smartlist-count-row${fieldsDisabled ? ' non-clickable' : ''}`}>
          <div className="col-xs-12">
            <button
              type="button"
              id="vfd-smartlist-btn"
              className={`btn btn-sync${!s.count ? ' warning' : ''} clickable vfd-smartlist-toggle-btn${s.isOpen ? ' active' : ''}`}
              onClick={toggleVfdSmartlist}
            >
              <span className="search-result">
                {s.count != null ? s.count : '    '} / {s.cmsCount != null ? s.cmsCount : ''}
              </span>
              <i className="icon-list btn-icon-sync">Smartlist</i>
            </button>
            <span
              className="icon-refresh-1 reset-btn vfd-smartlist-reset"
              style={{ marginLeft: 8 }}
              onClick={resetVfdSmartlist}
            >
              Reset
            </span>
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="vfd_mfg_id">Manufacturer</span>
            </div>
          </div>
          <div className="col-xs-7">
            {filterSelect('vfd_mfg_id', 'vfd_mfg_id', 'vfd_mfg', p.vfd_mfg_id,
              mfgRows.map((row) => ({
                key: String(row.id), value: String(row.id), label: String(row.name ?? ''),
              })))}
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="vfd_series_id">Series</span>
            </div>
          </div>
          <div className="col-xs-7">
            {filterSelect('vfd_series_id', 'vfd_series_id', 'vfd_series', p.vfd_series_id,
              seriesRows.map((row) => ({
                key: String(row.id), value: String(row.id), label: String(row.series ?? ''),
              })))}
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="model">Model</span>
            </div>
          </div>
          <div className="col-xs-7">
            {filterSelect('model', 'vfd_model', 'model', effModel, modelOptions)}
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="fla">Rating</span>
              <span className="unit right">Amp</span>
            </div>
          </div>
          <div className="col-xs-7">
            <div className="row">
              <div className="col-xs-6">
                {filterSelect('fla', 'vfd_rating', 'fla', p.fla,
                  (doc.fla ?? []).map((row) => ({
                    key: String(row.fla),
                    value: String(row.fla),
                    label: row.fla != null ? Number(row.fla).toFixed(2) : '',
                  })))}
              </div>
            </div>
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="description_id">Description</span>
            </div>
          </div>
          <div className="col-xs-7">
            {filterSelect('description_id', 'vfd_description_id', 'description', p.description_id,
              (doc.description ?? []).map((row) => ({
                key: String(row.id), value: String(row.id), label: String(row.name ?? ''),
              })))}
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="voltage_id">Voltage</span>
            </div>
          </div>
          <div className="col-xs-7">
            {/* voltage derived from motor, not user-editable — fan.selector.js:8504 */}
            <input
              type="text"
              id="voltage_id_display"
              readOnly
              tabIndex={-1}
              style={{ width: 'calc(100% - 5px)', paddingLeft: 5 }}
              placeholder="- -"
              disabled={fieldsDisabled}
              value={voltageName}
              onChange={() => undefined}
            />
            <input
              type="hidden"
              id="voltage_id"
              name="voltage_id"
              value={v?.voltage_id != null ? String(v.voltage_id) : 'None'}
            />
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="wiring_id">Wiring</span>
            </div>
          </div>
          <div className="col-xs-7">
            <select
              name="wiring_id"
              id="vfd_wiring_id"
              data-name="vfd_wiring"
              className="vfd_model_field"
              disabled={fieldsDisabled}
              value={selVal(v?.wiring_id)}
              onChange={(e) => onModelSelectChange('wiring_id', e.target.value)}
            >
              <option value="None">- -</option>
              {(doc.wiring ?? []).map((row) => (
                <option key={String(row.id)} value={String(row.id)}>{String(row.name ?? '')}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={leftColClass}>
          <div className="col-xs-5">
            <div className="row label-part">
              <span data-name="supplied_by_id">Supplied by</span>
            </div>
          </div>
          <div className="col-xs-7">
            <select
              name="supplied_by_id"
              id="vfd_supplied_by_id"
              data-name="vfd_supplied_by"
              className="vfd_model_field"
              disabled={fieldsDisabled}
              value={selVal(v?.supplied_by_id)}
              onChange={(e) => onModelSelectChange('supplied_by_id', e.target.value)}
            >
              <option value="None">- -</option>
              {(doc.supplied_by ?? []).map((row) => (
                <option key={String(row.id)} value={String(row.id)}>{String(row.name ?? '')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ===== right column: flip panel (tmpl 1293-1562) ===== */}
      <div className="col-lg-6 col-xs-12 vfd-flip-col" style={{ height: '100%', float: 'left' }}>
        <div className="vfd-mount-wrapper" id={`${idPrefix}vfd-flip`} data-tab={s.isOpen ? 'smartlist' : 'front'}>
          <div className="mounting-flip-card">
            <div className="mounting-flip-inner">
              <div className="mounting-flip-front right-column vfd-rating-right">
                {/* Configuration */}
                <div className="col-xs-12 vfd-rating-row">
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="configuration_id">VFD-to-Motor Configuration</span>
                    </div>
                  </div>
                  <div className="col-xs-7 ">
                    {/* Dev-sync H: options come from /vfds/configurations/
                        (mfg/series-filtered — loadVfdConfigurations) once a
                        load has run; before that, the full vfdCms list stands
                        in, exactly like the legacy tmpl render pre-replace. */}
                    <select
                      name="configuration_id"
                      id="configuration_id"
                      data-name="configuration"
                      className="vfd_model_field right-select"
                      disabled={fieldsDisabled}
                      value={selVal(v?.configuration_id)}
                      onChange={(e) => onModelSelectChange('configuration_id', e.target.value)}
                    >
                      <option value="None" className="hide">- -</option>
                      {(vfdConfigsRef.current ?? doc.configuration ?? []).map((row) => (
                        <option key={String(row.id)} value={String(row.id)} data-value={row.value}>
                          {String(row.name ?? '')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Quantity (readonly, qty_total chain) */}
                <div className="col-xs-12 vfd-rating-row">
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="vfd_qty_total">Quantity</span>
                    </div>
                  </div>
                  <div className="col-xs-7">
                    <input
                      type="text"
                      name="vfd_qty_total"
                      style={{ width: 'calc(100% - 5px)', paddingLeft: 5 }}
                      value={qtyTotalRef.current != null ? String(qtyTotalRef.current) : dash}
                      readOnly
                      tabIndex={-1}
                      disabled={fieldsDisabled}
                      onChange={() => undefined}
                    />
                  </div>
                </div>

                {/* Section (loadVfdSections js:8476-8502 — GET ahu/sections/?ahu_id=) */}
                <div className="col-xs-12 vfd-rating-row">
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="section_id">Section</span>
                    </div>
                  </div>
                  <div className="col-xs-7">
                    <select
                      name="section_id"
                      id="vfd_section"
                      data-name="section"
                      className="vfd_model_field right-select"
                      required
                      disabled={fieldsDisabled}
                      value={selVal(v?.section_id)}
                      onChange={(e) => onModelSelectChange('section_id', e.target.value)}
                    >
                      <option value="None" className="hide">- -</option>
                      {sectionRows.map((sec) => (
                        <option key={String(sec.id)} value={String(sec.id)}>
                          {String(sec.label ?? sec.id)}
                        </option>
                      ))}
                      {v?.section_id != null &&
                        !sectionRows.some((sec) => String(sec.id) === String(v.section_id)) && (
                          <option value={String(v.section_id)}>{String(v.section_id)}</option>
                        )}
                    </select>
                  </div>
                </div>

                {/* Mounting Arrangement — dev-sync I (fan.selector.tmpl:1366 on
                    merged dev): hidden for Annexair fans (hide-for-annexair +
                    the params_fan.mfg_id==237 render check) and for OJ VFDs
                    (vfd-oj-mount-row, updateVfdOjMountRowsVisibility
                    js:4617-4629 — render-derived here). */}
                <div className={`col-xs-12 vfd-rating-row vfd-oj-mount-row hide-for-annexair${isAnnexairFan || isOjVfd ? ' hide' : ''}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="mounting_arrangement_id">Mounting Arrangement</span>
                    </div>
                  </div>
                  <div className="col-xs-7">
                    <select
                      name="mounting_arrangement_id"
                      id="vfd_mounting_arrangement"
                      data-name="vfd_mounting_arrangement"
                      className="vfd_model_field right-select"
                      disabled={fieldsDisabled}
                      value={selVal(v?.mounting_arrangement_id)}
                      onChange={(e) => onModelSelectChange('mounting_arrangement_id', e.target.value)}
                    >
                      <option value="None" className="hide">- -</option>
                      {arrangementsRef.current.map((row) => (
                        <option key={String(row.id)} value={String(row.id)}>{String(row.name ?? '')}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bank W x H (readonly — filled from compute_position, js:8448-8461).
                    vfd-oj-mount-row (dev-sync I): hidden for OJ VFDs. */}
                <div className={`col-xs-12 vfd-rating-row vfd-dual-output-row vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    {/* ★ The `X` separator is an INLINE `.unit` (no `.right`
                        float), so it needs the inter-span space the legacy
                        tmpl gets for free from its newlines (tmpl:1437-1442).
                        JSX drops whitespace that spans a line break, which
                        rendered the label "Bank WidthXHeight" on dev3 against
                        dev's "Bank Width X Height". */}
                    <div className="row label-part">
                      <span data-name="vfd_bank_width">Bank Width</span>{' '}
                      <span className="unit">X</span>{' '}
                      <span data-name="vfd_bank_height">Height</span>{' '}
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="vfd-paired-fields">
                      <div className="vfd-pair-field">
                        <input type="text" name="vfd_bank_width" value={fmt2(v?.bank_width)}
                          readOnly tabIndex={-1} disabled={fieldsDisabled} onChange={() => undefined} />
                      </div>
                      <div className="vfd-pair-separator">
                        <span className="unit">X</span>
                      </div>
                      <div className="vfd-pair-field">
                        <input type="text" name="vfd_bank_height" value={fmt2(v?.bank_height)}
                          readOnly tabIndex={-1} disabled={fieldsDisabled} onChange={() => undefined} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Housed in Enclosure + enc W x H */}
                <div className={`col-xs-12 vfd-rating-row vfd-checkbox-row vfd-gap-after vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      {/* Same inline-`X` spacing as Bank Width above
                          (tmpl:1461-1467). The two empty spans render nothing,
                          so dev's collapsed label is "… W X H in". */}
                      <span data-name="housed_in_enclosure">Housed in Enclosure W</span>{' '}
                      <span className="unit">X</span>{' '}
                      <span>H</span>{' '}
                      <span data-name="enc_width" />
                      <span data-name="enc_height" />
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="vfd-enclosure-row">
                      <div className="vfd-enclosure-checkbox-wrap">
                        <label className="vfd-check-label vfd-enclosure-checkbox">
                          <input
                            type="checkbox"
                            name="housed_in_enclosure"
                            className="vfd_model_field"
                            disabled={fieldsDisabled}
                            checked={housed}
                            onChange={(e) => onHousedChange(e.target.checked)}
                          />
                        </label>
                      </div>
                      <div className={`vfd-enclosure-dims${housed ? '' : ' hide'}`}>
                        <div className="vfd-enc-field">
                          {/* dynamic data-min = (bank||0)+2*spacing — set by
                              applyVFDPositionResponse in legacy (js:8462-8469);
                              render-derived here. */}
                          <input
                            type="text"
                            name="enc_width"
                            data-step="0.01"
                            data-min={encMin('enc_width')}
                            autoComplete="off"
                            data-formatter_type="decimal"
                            data-type="number"
                            style={{ textAlign: 'left' }}
                            className={`vfd_model_field${encInvalid.current.enc_width ? ' invalid' : ''}`}
                            disabled={fieldsDisabled}
                            key={`encw-${String(v?.enc_width ?? '')}`}
                            defaultValue={v?.enc_width != null && v.enc_width !== '' ? fmt2(v.enc_width) : ''}
                            onBlur={(e) => commitEnc('enc_width', e.currentTarget)}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          />
                        </div>
                        <div className="vfd-enc-separator">
                          <span className="unit">X</span>
                        </div>
                        <div className="vfd-enc-field">
                          <input
                            type="text"
                            name="enc_height"
                            data-step="0.01"
                            data-min={encMin('enc_height')}
                            autoComplete="off"
                            data-formatter_type="decimal"
                            data-type="number"
                            style={{ textAlign: 'left' }}
                            className={`vfd_model_field${encInvalid.current.enc_height ? ' invalid' : ''}`}
                            disabled={fieldsDisabled}
                            key={`ench-${String(v?.enc_height ?? '')}`}
                            defaultValue={v?.enc_height != null && v.enc_height !== '' ? fmt2(v.enc_height) : ''}
                            onBlur={(e) => commitEnc('enc_height', e.currentTarget)}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bank Mounting Plane */}
                <div className={`col-xs-12 vfd-rating-row vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="mounting_plane_id">Bank Mounting Plane</span>
                    </div>
                  </div>
                  <div className="col-xs-7">
                    <select
                      name="mounting_plane_id"
                      id="mounting_plane_id"
                      data-name="vfd_mounting_plane"
                      className="vfd_position_field right-select"
                      disabled={positionDisabled}
                      value={selVal(pos.mounting_plane_id)}
                      onChange={(e) => onPlaneChange(e.target.value)}
                    >
                      <option value="None" className="hide">- -</option>
                      {(doc.mounting_plane ?? []).map((row) => (
                        <option key={String(row.id)} value={String(row.id)} data-value={row.value}>
                          {String(row.name ?? '')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* To Floor | Roof */}
                <div className={`col-xs-12 vfd-rating-row vfd-dual-output-row vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="position_y">To Floor</span> |{' '}
                      <span data-name="vfd_position_Hy">Roof</span>
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="row">
                      <div className="col-xs-4">
                        <input
                          type="text"
                          data-formatter_type="decimal"
                          autoComplete="off"
                          name="position_y"
                          className={`vfd_position_field${negWarn(pos.y) ? ' warning' : ''}`}
                          disabled={fieldsDisabled}
                          readOnly={autoY}
                          tabIndex={autoY ? -1 : undefined}
                          key={`posy-${String(pos.y ?? '')}-${autoY}`}
                          defaultValue={fmt2(pos.y)}
                          onBlur={(e) => { if (!autoY) commitPos('y', e.currentTarget); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                      </div>
                      <div className="col-xs-4">
                        {/* Roof (Hy) ← compute resp vfd.distance_to_roof (js:8438-8440) */}
                        <input type="text" name="vfd_position_Hy" value={fmt2(v?.distance_to_roof)}
                          className={negWarn(v?.distance_to_roof) ? 'warning' : undefined}
                          readOnly tabIndex={-1} disabled={fieldsDisabled} onChange={() => undefined} />
                      </div>
                      <div className="col-xs-4">
                        <label className="for-center-position">
                          <input
                            type="checkbox"
                            name="default_pos_y"
                            className="vfd_position_field"
                            disabled={fieldsDisabled}
                            checked={autoY}
                            onChange={(e) => onDefaultPosChange('default_pos_y', e.target.checked)}
                          />
                          <span data-name="default_pos_y">Auto-Position</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* To Wall Left | Right (planes 3/4) */}
                <div
                  id="vfd_wall_left_right_row"
                  className={`col-xs-12 vfd-rating-row vfd-dual-output-row vfd-oj-mount-row${isWallLeftRight && !isOjVfd && !isAnnexairFan ? '' : ' hide'}`}
                >
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="position_x">To Wall Left</span> |{' '}
                      <span data-name="vfd_position_Wx">Right</span>
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="row">
                      <div className="col-xs-4">
                        <input
                          type="text"
                          data-formatter_type="decimal"
                          autoComplete="off"
                          name="position_x"
                          className={`vfd_position_field${negWarn(pos.x) ? ' warning' : ''}`}
                          disabled={fieldsDisabled}
                          readOnly={autoX}
                          tabIndex={autoX ? -1 : undefined}
                          key={`posx-lr-${String(pos.x ?? '')}-${autoX}`}
                          defaultValue={fmt2(pos.x)}
                          onBlur={(e) => { if (!autoX) commitPos('x', e.currentTarget); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                      </div>
                      <div className="col-xs-4">
                        {/* Wall Right (Wx) ← compute resp vfd.distance_to_right_wall (js:8441-8443) */}
                        <input type="text" name="vfd_position_Wx" value={fmt2(v?.distance_to_right_wall)}
                          className={negWarn(v?.distance_to_right_wall) ? 'warning' : undefined}
                          readOnly tabIndex={-1} disabled={fieldsDisabled} onChange={() => undefined} />
                      </div>
                      <div className="col-xs-4">
                        <label className="for-center-position">
                          <input
                            type="checkbox"
                            name="default_pos_x"
                            className="vfd_position_field"
                            disabled={fieldsDisabled}
                            checked={autoX}
                            onChange={(e) => onDefaultPosChange('default_pos_x', e.target.checked)}
                          />
                          <span data-name="default_pos_x">Auto-Position</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* To Wall Ups | Downs (other planes) */}
                <div
                  id="vfd_wall_ups_downs_row"
                  className={`col-xs-12 vfd-rating-row vfd-dual-output-row vfd-oj-mount-row${isWallLeftRight || isOjVfd || isAnnexairFan ? ' hide' : ''}`}
                >
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="position_x">To Wall Ups</span> |{' '}
                      <span data-name="vfd_position_Lx">Downs</span>
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="row">
                      <div className="col-xs-4">
                        <input
                          type="text"
                          data-formatter_type="decimal"
                          autoComplete="off"
                          name="position_x"
                          className={`vfd_position_field${negWarn(pos.x) ? ' warning' : ''}`}
                          disabled={fieldsDisabled}
                          readOnly={autoX}
                          tabIndex={autoX ? -1 : undefined}
                          key={`posx-ud-${String(pos.x ?? '')}-${autoX}`}
                          defaultValue={fmt2(pos.x)}
                          onBlur={(e) => { if (!autoX) commitPos('x', e.currentTarget); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                      </div>
                      <div className="col-xs-4">
                        {/* Wall Downs (Lx) ← compute resp vfd.distance_to_downstream_wall (js:8444-8446) */}
                        <input type="text" name="vfd_position_Lx" value={fmt2(v?.distance_to_downstream_wall)}
                          className={negWarn(v?.distance_to_downstream_wall) ? 'warning' : undefined}
                          readOnly tabIndex={-1} disabled={fieldsDisabled} onChange={() => undefined} />
                      </div>
                      <div className="col-xs-4">
                        <label className="for-center-position">
                          <input
                            type="checkbox"
                            name="default_pos_x"
                            className="vfd_position_field"
                            disabled={fieldsDisabled}
                            checked={autoX}
                            onChange={(e) => onDefaultPosChange('default_pos_x', e.target.checked)}
                          />
                          <span data-name="default_pos_x">Auto-Position</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Casing Mount radios */}
                <div className={`col-xs-12 vfd-rating-row vfd-radio-row vfd-gap-after vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="casing_mount_id">Casing Mount</span>
                    </div>
                  </div>
                  <div className="col-xs-7">
                    <div className="row vfd-radio-columns">
                      {(doc.casing_mount ?? []).map((row) => (
                        <div className="col-xs-6" key={String(row.id)}>
                          <label
                            data-casing={row.id}
                            className={`vfd-radio-label casing_mount_radio${!housed && Number(row.id) == 2 ? ' non-clickable' : ''} `}
                          >
                            <input
                              type="radio"
                              name="casing_mount_id"
                              data-value={row.value}
                              value={String(row.id)}
                              className="vfd_model_field"
                              disabled={positionDisabled}
                              checked={Number(v?.casing_mount_id) == Number(row.id)}
                              onChange={(e) => onModelSelectChange('casing_mount_id', e.target.value)}
                            />
                            <span>{String(row.name ?? '')}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Spacing Hor x Ver */}
                <div className={`col-xs-12 vfd-rating-row vfd-dual-output-row vfd-oj-mount-row${ojHide}`}>
                  <div className="col-xs-5">
                    <div className="row label-part">
                      <span data-name="spacing_horizontal">Spacing Hor X </span>
                      <span data-name="spacing_vertical">Ver</span>
                      <span className="unit right">in</span>
                    </div>
                  </div>
                  <div className="col-xs-7 vfd-rating-field">
                    <div className="vfd-paired-fields">
                      <div className="vfd-pair-field">
                        <input
                          type="text"
                          name="spacing_horizontal"
                          data-name="spacing_horizontal"
                          data-min={2}
                          data-max={12}
                          data-formatter_type="decimal"
                          autoComplete="off"
                          className={`vfd_model_field${spacingInvalid.current.horizontal ? ' invalid' : ''}`}
                          disabled={positionDisabled}
                          key={`sph-${String(v?.spacing_horizontal ?? '')}`}
                          defaultValue={formatDecimalDisplay(v?.spacing_horizontal ?? 2)}
                          onBlur={(e) => commitSpacing('horizontal', e.currentTarget)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                      </div>
                      <div className="vfd-pair-separator">
                        <span className="unit">X</span>
                      </div>
                      <div className="vfd-pair-field">
                        <input
                          type="text"
                          name="spacing_vertical"
                          data-name="spacing_vertical"
                          data-min={2}
                          data-max={12}
                          data-formatter_type="decimal"
                          autoComplete="off"
                          className={`vfd_model_field${spacingInvalid.current.vertical ? ' invalid' : ''}`}
                          disabled={positionDisabled}
                          key={`spv-${String(v?.spacing_vertical ?? '')}`}
                          defaultValue={formatDecimalDisplay(v?.spacing_vertical ?? 2)}
                          onBlur={(e) => commitSpacing('vertical', e.currentTarget)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /.mounting-flip-front */}

              <div className="mounting-flip-back">
                <div className="mounting-smartlist-content">
                  <div className="">
                    <div className="filter-list">
                      <div className="col-xs-12 padding-right-0">
                        <ul className="list-heading list-inline vfd-smartlist-heading" ref={headingRef}>
                          <li className="custom-columns">
                            <h4 className="w-130">MANUFACTURER</h4>
                            <h4 className="w-110">SERIES</h4>
                            <h4 className="w-150">MODEL</h4>
                            <h4 className="w-120 text-align-right">POWER (HP)</h4>
                            <h4 className="ml-40 w-220">DESCRIPTION</h4>
                            <h4 className="w-130 text-align-right">
                              FLA <span style={{ textTransform: 'none' }}>(Amp)</span>
                            </h4>
                            <h4 className="ml-50 w-170">NEMA</h4>
                          </li>
                        </ul>
                        <ul
                          className="list-rows scrollable catalogue-rows-container smartlist-items vfd-smartlist-items"
                          tabIndex={0}
                          ref={listRef}
                          onScroll={onListScroll}
                        >
                          {s.isLoading && s.data.length === 0 ? (
                            <li className="smartlist-center-spinner">
                              <div className="preloader dotted-preloader active" />
                            </li>
                          ) : s.data.length === 0 ? (
                            <li className="smartlist-no-results">No results</li>
                          ) : (
                            <>
                              {s.data.map((item) => (
                                <li
                                  key={item.id}
                                  className="smartlist-row vfd-smartlist-row"
                                  data-id={item.id}
                                  onClick={() => selectFromVfdSmartlist(item, false)}
                                >
                                  <ul className="table-item">
                                    <li className="xxlong text-overflow-unset">
                                      <span className="only-for-vfd-row custom-columns-row">
                                        <span className="w-130">{item.mfg_name ?? ''}</span>
                                        <span className="w-110">{item.series_name ?? ''}</span>
                                        <span className="w-150">{item.model ?? ''}</span>
                                        <span className="w-120 text-align-right">
                                          {item.power != null ? String(item.power) : ''}
                                        </span>
                                        <span
                                          className="ml-40 w-220 col-desc-wrapper"
                                          title={item.description_name || undefined}
                                        >
                                          <span className="col-truncate">{item.description_name ?? ''}</span>
                                        </span>
                                        <span className="w-130 text-align-right">
                                          {item.fla != null ? Number(item.fla).toFixed(2) : ''}
                                        </span>
                                        <span className="ml-50 w-170">{item.nema_encl_type_name ?? ''}</span>
                                      </span>
                                    </li>
                                  </ul>
                                </li>
                              ))}
                              {s.isLoading && <li className="smartlist-bottom-spinner" />}
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /.mounting-flip-back */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FanVfdTab;
